/**
 * Overview tab (Phase 5 Action 49).
 *
 * Combines the "basics" dl and the "activation" checklist that used to
 * live inline on the old single-page TenantDetail. This is the default
 * landing tab so it should communicate "what is this tenant, and is it
 * live" at a glance.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import { apiPath } from '../../api/baseUrl';
import { getStoredToken } from '../../auth/AuthContext';
import { fetchJson, formatDate, type TenantContext } from './shared';

interface PrereqCheck {
  status: 'provisioning' | 'active' | 'suspended' | 'trial' | 'deleted';
  planAssigned: boolean;
  slackConnected: boolean;
  adminInvited: boolean;
  ready: boolean;
}

export default function Overview() {
  const { tenant } = useOutletContext<TenantContext>();
  const queryClient = useQueryClient();

  const prereqQ = useQuery({
    queryKey: ['tenant-activation-prereqs', tenant.id],
    queryFn: () => fetchJson<{ data: PrereqCheck }>(`/api/provisioning/tenants/${tenant.id}/activation-prereqs`),
  });

  const activateMutation = useMutation({
    mutationFn: async () => {
      const token = getStoredToken();
      const res = await fetch(apiPath(`/api/provisioning/tenants/${tenant.id}/activate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        let message = `activate failed: ${res.status}`;
        try {
          const b = (await res.json()) as { message?: string };
          if (b.message) message = b.message;
        } catch { /* ignore */ }
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['tenant-activation-prereqs', tenant.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  function handleActivate() {
    if (!confirm('Activate this tenant? The Slack bot will start accepting reports immediately.')) return;
    activateMutation.mutate();
  }

  return (
    <>
      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Basics</h2>
        </header>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 px-6 py-4 text-sm">
          <div>
            <dt className="text-slate-500">Primary domain</dt>
            <dd className="mt-0.5 text-slate-900">{tenant.primaryDomain ?? <span className="text-slate-400">—</span>}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Timezone</dt>
            <dd className="mt-0.5 text-slate-900 font-mono">{tenant.timezone}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Locale</dt>
            <dd className="mt-0.5 text-slate-900 font-mono">{tenant.locale}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Created</dt>
            <dd className="mt-0.5 text-slate-900">{formatDate(tenant.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Activated</dt>
            <dd className="mt-0.5 text-slate-900">{formatDate(tenant.activatedAt)}</dd>
          </div>
        </dl>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Activation</h2>
        </header>
        <div className="px-6 py-4 text-sm">
          {prereqQ.isLoading && <div className="text-slate-500">Loading checklist…</div>}
          {prereqQ.data && (() => {
            const c = prereqQ.data.data;
            if (c.status === 'active') {
              return (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">
                  <strong>Tenant is active.</strong> The Slack bot will accept reports from this workspace.
                  {tenant.activatedAt && (
                    <span className="ml-1 text-emerald-800">Activated {formatDate(tenant.activatedAt)}.</span>
                  )}
                </div>
              );
            }
            if (c.status === 'suspended' || c.status === 'deleted') {
              return (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
                  Tenant is {c.status}. Reactivation is a future flow.
                </div>
              );
            }
            const items: Array<{ done: boolean; label: string; hint: string }> = [
              { done: c.planAssigned,    label: 'Plan assigned',   hint: 'Pick a plan in the Config tab.' },
              { done: c.slackConnected,  label: 'Slack connected', hint: 'Run the Slack install flow from the Config tab.' },
              { done: c.adminInvited,    label: 'Admin invited',   hint: 'Send a magic link from the Users tab.' },
            ];
            const missingCount = items.filter((i) => !i.done).length;
            return (
              <>
                <p className="mb-3 text-slate-600">
                  All three prerequisites must be complete before activation. Activation flips status to <code className="font-mono">active</code> and unlocks the customer side.
                </p>
                <ul className="space-y-2">
                  {items.map((i) => (
                    <li key={i.label} className="flex items-start gap-2">
                      <span className={clsx('mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-xs', i.done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500')}>
                        {i.done ? '✓' : '○'}
                      </span>
                      <span>
                        <span className={i.done ? 'text-slate-900' : 'text-slate-600'}>{i.label}</span>
                        {!i.done && <span className="ml-2 text-xs text-slate-500">— {i.hint}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                {activateMutation.isError && (
                  <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-800">
                    {activateMutation.error.message}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={handleActivate}
                    disabled={!c.ready || activateMutation.isPending}
                    className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {activateMutation.isPending
                      ? 'Activating…'
                      : !c.ready
                        ? `Complete ${missingCount} step${missingCount === 1 ? '' : 's'} above`
                        : 'Activate tenant'}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </section>
    </>
  );
}
